import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
} from "@mui/material";
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import SnackBar from "./SnackBar";
import { get_admin_plans, update_admin_plan } from "../Api service/APIvariables";

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editPrice, setEditPrice] = useState("");
  const [editYearlyPrice, setEditYearlyPrice] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Snackbar state
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackStatus, setSnackStatus] = useState(false);
  const [snackColor, setSnackColor] = useState(false);

  const fetchPlans = async () => {
    try {
      const user_id = localStorage.getItem("user_id");
      const token = localStorage.getItem("auth_token");
      if (!user_id || !token) return;

      const formData = new FormData();
      formData.append("user_id", user_id);

      const res = await fetch(get_admin_plans, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!data.error) {
        setPlans(data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleEditClick = (plan) => {
    setSelectedPlan(plan);
    setEditPrice(plan.price);
    setEditYearlyPrice(plan.yearly_price || "0");
    setEditOpen(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const user_id = localStorage.getItem("user_id");
      const token = localStorage.getItem("auth_token");
      if (!user_id || !token || !selectedPlan) return;

      const formData = new FormData();
      formData.append("user_id", user_id);
      formData.append("plan_id", selectedPlan.id);
      formData.append("price", editPrice);
      formData.append("yearly_price", editYearlyPrice);

      const res = await fetch(update_admin_plan, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!data.error) {
        setSnackMessage("Plan details updated successfully!");
        setSnackStatus(true);
        setSnackColor(true);
        setSnackOpen(true);
        setEditOpen(false);
        fetchPlans(); // refresh
      } else {
        setSnackMessage(data.message || "Failed to update plan");
        setSnackStatus(false);
        setSnackColor(false);
        setSnackOpen(true);
      }
    } catch (err) {
      console.error(err);
      setSnackMessage("An error occurred");
      setSnackStatus(false);
      setSnackColor(false);
      setSnackOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ padding: 4, width: "100%", maxWidth: "1200px", margin: "0 auto" }}>
      <SnackBar
        open={snackOpen}
        message={snackMessage}
        setOpen={setSnackOpen}
        status={snackStatus}
        color={snackColor}
      />

      <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, color: "#453c72" }}>
        Subscription Plans
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 4 }}>
        Manage base pricing (monthly/yearly) for all subscription tiers.
      </Typography>

      {isLoading ? (
        <LinearProgress color="secondary" sx={{ borderRadius: 2 }} />
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflowX: "auto" }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell><strong>ID</strong></TableCell>
                <TableCell><strong>Plan Name</strong></TableCell>
                <TableCell align="right"><strong>Monthly Price (₹)</strong></TableCell>
                <TableCell align="right"><strong>Yearly Price (₹)</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan.id} hover>
                  <TableCell>{plan.id}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{plan.name}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    ₹{parseFloat(plan.price).toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    ₹{parseFloat(plan.yearly_price || 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      startIcon={<EditRoundedIcon />}
                      onClick={() => handleEditClick(plan)}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold", color: "#453c72" }}>
          Edit {selectedPlan?.name} Plan Prices
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Monthly Price (₹)"
            type="number"
            fullWidth
            value={editPrice}
            onChange={(e) => setEditPrice(e.target.value)}
            sx={{ mb: 3, mt: 1 }}
            disabled={isSaving}
          />
          <TextField
            label="Yearly Price (₹)"
            type="number"
            fullWidth
            value={editYearlyPrice}
            onChange={(e) => setEditYearlyPrice(e.target.value)}
            disabled={isSaving}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setEditOpen(false)} color="inherit" disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="secondary" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
